import React, {createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState} from 'react';

import {conversionEventsBatches, fetchAccountActions, fetchCandidates, fetchClientProposals,
        fetchConversionEvents, fetchSuggestedBudgets, prepareRecentSpend} from './api';
import {EVENTS_OK, EVENTS_UNAVAILABLE} from './conversionSuggestion';
import {cachedServerFor, cachedStateFor, ownerKey, rememberServer, rememberState} from './wizardMemory';
import {initialState, reconcile, wizardReducer} from './wizardReducer';
import {loadDecisions, saveDecisions} from './wizardStorage';

/**
 * El estado del túnel de alta mientras dura el recorrido.
 *
 * Vive en un contexto y no en cada pantalla porque el recorrido es uno solo: lo que el usuario
 * decide sobre sus clientes en el paso 3 es lo que se le va a cobrar presupuesto en el paso 4 y lo
 * que se va a crear al final. Pero lo que se GUARDA son sólo sus decisiones — las cuentas, las
 * propuestas y los presupuestos se vuelven a pedir, porque entre un paso y otro alguien más de la
 * agencia pudo conectar una plataforma o armar una línea.
 */

const WizardContext = createContext(null);

/**
 * Lo último que contestó el servidor, fuera de React.
 *
 * Cada paso del túnel es una pantalla propia, así que al pasar de uno a otro el proveedor se monta
 * de nuevo y perdería lo que ya había traído: el paso de presupuestos necesita las cuentas que trajo
 * el de clientes. Guardarlo acá lo hace sobrevivir a la navegación dentro del recorrido, pero NO a
 * una recarga —ahí se vuelve a pedir todo, que es lo correcto: si el usuario recargó, lo que
 * tenemos en memoria ya no es confiable.
 *
 * ⚠️ **Lleva de quién son los datos.** Esto vive mientras viva la pestaña, y cerrar sesión no
 * recarga la página: sin la marca, quien entra después con otra cuenta vería, aunque sea el
 * instante hasta que llegue el primer fetch, las cuentas y los clientes de la agencia anterior.
 */

export function useOnboarding() {
    const context = useContext(WizardContext);
    if (!context) throw new Error('useOnboarding necesita estar dentro de <OnboardingProvider>');
    return context;
}

export function OnboardingProvider({children}) {
    const owner = useRef(ownerKey());
    const [state, dispatch] = useReducer(wizardReducer, undefined, () => {
        // Primero lo que quedó de la navegación anterior (incluye el resultado del último apply) y
        // recién si no hay, el borrador guardado.
        const live = cachedStateFor(owner.current);
        if (live) return live;
        const stored = loadDecisions();
        const base = initialState();
        return stored ? {...base, ...stored} : base;
    });

    useEffect(() => {
        rememberState(state, owner.current);
    }, [state]);
    const [server, setServerState] = useState(() => cachedServerFor(owner.current));
    const setServer = useCallback(update => {
        setServerState(previous => {
            const next = typeof update === 'function' ? update(previous) : update;
            rememberServer(next, owner.current);
            return next;
        });
    }, []);
    // Lo último del servidor, para los refrescos que tienen que comparar contra lo anterior sin
    // depender del render (ver `refreshProposals`).
    const serverRef = useRef(server);
    serverRef.current = server;
    const [loading, setLoading] = useState({candidates: false, proposals: false, budgets: false});
    const saveTimer = useRef(null);

    // Las decisiones se guardan con un respiro: escribir en cada tecla del nombre de un cliente
    // sería una escritura por pulsación. Lo pendiente se vuelca al desmontar — antes el cleanup
    // sólo cancelaba el temporizador, así que tildar algo y apretar «Continuar» en menos de medio
    // segundo hacía que el paso siguiente leyera el borrador viejo.
    const pending = useRef(null);
    pending.current = {decisions: state.decisions, budgetEdits: state.budgetEdits,
                       conversionEdits: state.conversionEdits, grouping: state.grouping};

    useEffect(() => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => saveDecisions(pending.current), 500);
        return () => clearTimeout(saveTimer.current);
    }, [state.decisions, state.budgetEdits, state.conversionEdits, state.grouping]);

    useEffect(() => () => saveDecisions(pending.current), []);

    const loadCandidates = useCallback(() => {
        setLoading(previous => ({...previous, candidates: true}));
        return fetchCandidates().then(response => {
            setLoading(previous => ({...previous, candidates: false}));
            if (response?.status !== 'OK') return null;
            setServer(previous => ({...previous, candidates: response.data}));
            return response.data;
        });
    }, [setServer]);

    /**
     * Trae las cuentas y las propuestas juntas y reconcilia una sola vez.
     *
     * Las dos cosas hablan de las mismas cuentas, así que reconciliar con una sola a mano sería
     * decidir con la mitad de la información: las propuestas dicen de quién es cada cuenta, y las
     * candidatas dicen cuáles siguen estando disponibles.
     */
    const loadProposals = useCallback(() => {
        setLoading(previous => ({...previous, proposals: true, candidates: true}));
        return Promise.all([fetchCandidates(), fetchClientProposals()]).then(([candidates, proposals]) => {
            setLoading(previous => ({...previous, proposals: false, candidates: false}));
            const candidatesData = candidates?.status === 'OK' ? candidates.data : null;
            const proposalsData = proposals?.status === 'OK' ? proposals.data : null;
            setServer(previous => ({...previous, candidates: candidatesData || previous.candidates,
                                    proposals: proposalsData || previous.proposals}));
            if (candidatesData) {
                dispatch({type: 'RECONCILE', candidates: candidatesData, proposals: proposalsData});
            }
            return {candidates: candidatesData, proposals: proposalsData};
        });
    }, [setServer]);

    /**
     * Vuelve a pedir SÓLO la propuesta, mientras el backend trae el gasto de 7 días (C-02).
     *
     * Las cuentas candidatas no cambian entre un refresco y otro: pedirlas de nuevo sería repetir la
     * consulta más cara del paso. El reconcile recibe la propuesta anterior (`refreshFrom`) para mover
     * sólo las filas que el usuario no tocó. Devuelve la propuesta nueva, o `null` si no llegó.
     */
    const refreshProposals = useCallback(() => fetchClientProposals().then(response => {
        if (response?.status !== 'OK') return null;
        const previous = serverRef.current || {};
        setServer(current => ({...current, proposals: response.data}));
        if (previous.candidates) {
            dispatch({type: 'RECONCILE', candidates: previous.candidates, proposals: response.data,
                      refreshFrom: previous.proposals || null});
        }
        return response.data;
    }), [setServer]);

    /**
     * Las acciones de conversión de una cuenta de Google (C-01), para el selector del paso 4.
     *
     * Quedan en la memoria del túnel (`server.accountActions[pk]`), con el dueño de los datos, para que
     * la fila pueda nombrar lo elegido aunque el selector se cierre o el desglose se pliegue. Una
     * respuesta que no llegó queda marcada con `status: 'ERROR'`, para poder reintentar.
     */
    const loadAccountActions = useCallback(pk => fetchAccountActions(pk).then(response => {
        const entry = response?.status === 'OK' && Array.isArray(response.data)
            ? {status: 'OK', actions: response.data}
            : {status: 'ERROR', actions: []};
        setServer(previous => ({...previous,
                                accountActions: {...(previous.accountActions || {}), [String(pk)]: entry}}));
        return entry;
    }, () => ({status: 'ERROR', actions: []})), [setServer]);

    const loadBudgets = useCallback(pks => {
        setLoading(previous => ({...previous, budgets: true}));
        return fetchSuggestedBudgets(pks).then(response => {
            setLoading(previous => ({...previous, budgets: false}));
            if (response?.status !== 'OK') return null;
            setServer(previous => ({...previous, budgets: response.data.budgets, period: response.data.period}));
            // `partial` viaja al lado de `data`, no adentro: devolver `response.data` pelado dejaba
            // al paso leyendo un campo que nunca existía, así que el aviso de «faltan montos porque
            // una tanda se cayó» no se mostró nunca.
            return {...response.data, partial: Boolean(response.partial)};
        });
    }, [setServer]);

    /**
     * Los eventos que registró cada cuenta de Meta, para proponer su conversión.
     *
     * Las tandas van EN SERIE y cada una se suma a lo que ya había apenas llega: la primera página se
     * pinta sin esperar a toda la cartera, y no salen N pedidos a la vez contra Meta. Una tanda caída
     * no corta las demás: sus cuentas quedan marcadas `UNAVAILABLE` y la fila ofrece reintentar. Se
     * marcan en vez de dejarlas sin entrada porque «sin entrada» también es «todavía en camino», y el
     * reintento tiene que poder distinguirlas para no pedir de nuevo lo que ya está saliendo.
     *
     * Lo que se vuelve a pedir se OLVIDA primero: sin eso, en un reintento la fila seguía diciendo
     * «No pudimos leer…» con su «Reintentar» hasta que llegaba la respuesta, el clic no se notaba y
     * se volvía a apretar (QA-conv3-04). Sin entrada, la fila muestra «Buscando…».
     */
    const loadConversionEvents = useCallback(async pks => {
        const keys = (pks || []).map(String);
        setServer(previous => {
            const known = previous.conversionEvents || {};
            if (!keys.some(key => key in known)) return previous;
            const rest = {...known};
            keys.forEach(key => { delete rest[key]; });
            return {...previous, conversionEvents: rest};
        });
        for (const batch of conversionEventsBatches(pks)) {
            // eslint-disable-next-line no-await-in-loop
            const response = await fetchConversionEvents(batch);
            const events = response?.status === EVENTS_OK
                ? response.data?.events || {}
                : Object.fromEntries(batch.map(pk => [String(pk), {status: EVENTS_UNAVAILABLE, actions: []}]));
            setServer(previous => ({...previous,
                                    conversionEvents: {...(previous.conversionEvents || {}), ...events}}));
        }
    }, [setServer]);

    const value = useMemo(() => ({
        state, dispatch, server, loading, loadCandidates, loadProposals, refreshProposals, prepareRecentSpend,
        loadBudgets, loadConversionEvents, loadAccountActions,
    }), [state, server, loading, loadCandidates, loadProposals, refreshProposals, loadBudgets,
        loadConversionEvents, loadAccountActions]);

    return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

/** Reconcilia contra lo último que contestó el servidor. Lo usan los pasos al montar. */
export function reconciledWith(state, server) {
    return reconcile(state, {candidates: server.candidates, proposals: server.proposals});
}
